import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import Avatar from "../components/Avatar";
import { API } from "../api";

function Soldes() {
  const { token, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [saved, setSaved] = useState({});
  const [editing, setEditing] = useState({});
  const [historyOpen, setHistoryOpen] = useState({});
  const [histories, setHistories] = useState({});

  useEffect(() => {
    const headers = { Authorization: "Bearer " + token };
    const url = user.role === "daf"
      ? `${API}/users`
      : `${API}/users/team`;
    axios.get(url, { headers }).then(r => {
      setMembers(r.data.filter(u => u.role === "collab" || u.role === "assistante" || u.role === "juriste" || u.role === "daf"));
    });
    axios.get(`${API}/leaves/team`, { headers }).then(r => {
      setLeaves(r.data.filter(l => l.statut === "approved"));
    });
  }, [token, user.role]);

  // Seuls les CP sont décomptés du solde (RTT/Maladie/Maternité/Paternité = orange = sans décompte)
  const joursPoser = (userId) => leaves
    .filter(l => l.user_id === userId && l.type === "CP")
    .reduce((sum, l) => sum + parseFloat(l.jours || 0), 0);

  const handleSave = async (m) => {
    await axios.put(`${API}/users/${m.id}/balance`,
      { cp_restants: m.cp_restants, cp_total: m.cp_total },
      { headers: { Authorization: "Bearer " + token } }
    );
    setSaved(s => ({ ...s, [m.id]: true }));
    setEditing(e => ({ ...e, [m.id]: false }));
    setTimeout(() => setSaved(s => ({ ...s, [m.id]: false })), 3000);
  };

  const handleChange = (id, value) => {
    setMembers(members.map(m => m.id === id ? { ...m, cp_total: value } : m));
  };

  const toggleHistory = async (uid) => {
    const isOpen = historyOpen[uid];
    setHistoryOpen(h => ({ ...h, [uid]: !isOpen }));
    if (!isOpen && !histories[uid]) {
      try {
        const r = await axios.get(`${API}/users/${uid}/balance-history`,
          { headers: { Authorization: "Bearer " + token } });
        setHistories(h => ({ ...h, [uid]: r.data }));
      } catch {
        setHistories(h => ({ ...h, [uid]: [] }));
      }
    }
  };

  // Stats globales
  const totalMembers = members.length;
  const avgSolde = totalMembers > 0
    ? (members.reduce((sum, m) => sum + parseFloat(m.cp_restants ?? 0), 0) / totalMembers).toFixed(1)
    : 0;
  const alertes = members.filter(m => parseFloat(m.cp_restants ?? 0) <= 5).length;

  const getBarColor = (pct, isNeg) => {
    if (isNeg) return "#A32D2D";
    if (pct <= 20) return "#A32D2D";
    if (pct <= 40) return "#854F0B";
    if (pct <= 70) return "#185FA5";
    return "#3B6D11";
  };

  return (
    <div>
      <h2 style={{ marginBottom: 20, fontWeight: 600, fontSize: 18 }}>💰 Soldes équipe</h2>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { label: "Collaborateurs", value: totalMembers, color: "#185FA5", bg: "#E6F1FB", icon: "👥" },
          { label: "Solde moyen", value: avgSolde + " j", color: "#3B6D11", bg: "#EAF3DE", icon: "📊" },
          { label: alertes > 0 ? "Alertes solde ≤ 5j" : "Aucune alerte", value: alertes, color: alertes > 0 ? "#A32D2D" : "#3B6D11", bg: alertes > 0 ? "#FCEBEB" : "#EAF3DE", icon: alertes > 0 ? "⚠️" : "✅" },
        ].map(k => (
          <div key={k.label} style={{ background: k.bg, borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 22 }}>{k.icon}</span>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: k.color, lineHeight: 1 }}>{k.value}</div>
              <div style={{ fontSize: 11, color: k.color, opacity: 0.8, marginTop: 2 }}>{k.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Liste */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {members.length === 0 && (
          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 10, padding: 32, textAlign: "center", color: "#aaa", fontSize: 13 }}>
            Aucun collaborateur trouvé.
          </div>
        )}
        {members.map(m => {
          const solde = parseFloat(m.cp_restants ?? 0);
          const total = parseFloat(m.cp_total) || 30;
          const isNeg = solde < 0;
          const pct = isNeg ? 0 : Math.max(0, Math.min(100, (solde / total) * 100));
          const barColor = getBarColor(pct, isNeg);
          const isEditing = editing[m.id];
          const isSaved = saved[m.id];
          const alerte = solde <= 5;
          const hist = histories[m.id] || [];

          return (
            <div key={m.id} style={{
              background: "#fff",
              border: `1px solid ${isNeg ? "#A32D2D" : alerte ? "#F09595" : "#e8e8e8"}`,
              borderRadius: 12,
              padding: "16px 20px",
              boxShadow: isNeg ? "0 0 0 3px rgba(163,45,45,0.10)" : alerte ? "0 0 0 3px rgba(163,45,45,0.06)" : "0 1px 3px rgba(0,0,0,0.04)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>

                {/* Avatar + nom */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 160 }}>
                  <Avatar prenom={m.prenom} nom={m.nom} size={36} fontSize={13} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#222" }}>{m.prenom} {m.nom}</div>
                    <div style={{ fontSize: 11, color: "#999", marginTop: 1, textTransform: "capitalize" }}>{m.role}</div>
                  </div>
                  {isNeg && <span style={{ fontSize: 10, background: "#A32D2D", color: "#fff", padding: "2px 8px", borderRadius: 10, fontWeight: 700, marginLeft: 4 }}>🚨 Solde négatif</span>}
                  {!isNeg && alerte && <span style={{ fontSize: 10, background: "#FCEBEB", color: "#A32D2D", padding: "2px 8px", borderRadius: 10, fontWeight: 700, marginLeft: 4 }}>⚠️ Alerte</span>}
                </div>

                {/* Barre de progression */}
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#888", marginBottom: 5 }}>
                    <span style={{ color: isNeg ? "#A32D2D" : undefined, fontWeight: isNeg ? 700 : 400 }}>
                      {isNeg ? `⚠️ Dépassement de ${Math.abs(solde)}j` : `${joursPoser(m.id)} j posés`}
                    </span>
                    <span style={{ fontWeight: 600, color: barColor }}>{solde} j restants / {total} j</span>
                  </div>
                  <div style={{ height: 8, background: "#f0f0f0", borderRadius: 10, overflow: "hidden" }}>
                    {isNeg
                      ? <div style={{ height: "100%", width: "100%", background: "repeating-linear-gradient(45deg,#A32D2D,#A32D2D 4px,#FCEBEB 4px,#FCEBEB 8px)", borderRadius: 10 }} />
                      : <div style={{ height: "100%", width: pct + "%", background: `linear-gradient(90deg, ${barColor}cc, ${barColor})`, borderRadius: 10, transition: "width 0.4s ease" }} />
                    }
                  </div>
                  <div style={{ fontSize: 10, color: isNeg ? "#A32D2D" : "#bbb", marginTop: 3, textAlign: "right", fontWeight: isNeg ? 700 : 400 }}>
                    {isNeg ? "Solde épuisé" : `${Math.round(pct)}% restant`}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  {isEditing ? (
                    <>
                      <div style={{ fontSize: 11, color: "#888" }}>Dotation :</div>
                      <input
                        type="number" value={m.cp_total || 30} min={0} max={100}
                        onChange={e => handleChange(m.id, e.target.value)}
                        style={{ width: 60, padding: "5px 8px", border: "1px solid #7C6FE0", borderRadius: 6, fontSize: 13, fontWeight: 600, color: "#333", outline: "none", textAlign: "center" }}
                        autoFocus
                      />
                      <button onClick={() => handleSave(m)}
                        style={{ background: "#7C6FE0", color: "#fff", border: "none", borderRadius: 6, padding: "5px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                        ✓ Enregistrer
                      </button>
                      <button onClick={() => setEditing(e => ({ ...e, [m.id]: false }))}
                        style={{ background: "#f5f5f5", color: "#888", border: "1px solid #ddd", borderRadius: 6, padding: "5px 10px", fontSize: 12, cursor: "pointer" }}>
                        Annuler
                      </button>
                    </>
                  ) : (
                    <>
                      {isSaved && <span style={{ fontSize: 12, color: "#3B6D11", background: "#EAF3DE", padding: "3px 10px", borderRadius: 6, fontWeight: 600 }}>✓ Enregistré</span>}
                      <button onClick={() => toggleHistory(m.id)}
                        style={{ background: "#f5f5f5", color: "#555", border: "1px solid #e0e0e0", borderRadius: 6, padding: "5px 10px", fontSize: 12, cursor: "pointer", fontWeight: 500 }}>
                        📋 {historyOpen[m.id] ? "Masquer" : "Historique"}
                      </button>
                      <button onClick={() => setEditing(e => ({ ...e, [m.id]: true }))}
                        style={{ background: "#f5f5f5", color: "#555", border: "1px solid #e0e0e0", borderRadius: 6, padding: "5px 14px", fontSize: 12, cursor: "pointer", fontWeight: 500 }}>
                        ✏️ Modifier
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Historique des mouvements */}
              {historyOpen[m.id] && (
                <div style={{ marginTop: 14, borderTop: "1px solid #f0f0f0", paddingTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Historique des mouvements</div>
                  {hist.length === 0 ? (
                    <div style={{ fontSize: 12, color: "#bbb", fontStyle: "italic" }}>Aucun mouvement enregistré</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {hist.map(h => (
                        <div key={h.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, padding: "5px 8px", background: h.delta < 0 ? "#FFF5F5" : "#F5FBF0", borderRadius: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 14 }}>{h.delta < 0 ? "➖" : "➕"}</span>
                            <div>
                              <div style={{ fontWeight: 600, color: "#333" }}>{h.motif}</div>
                              {h.leave_type && <div style={{ fontSize: 11, color: "#888" }}>{h.leave_type} · {new Date(h.date_debut).toLocaleDateString("fr-FR")} → {new Date(h.date_fin).toLocaleDateString("fr-FR")}</div>}
                            </div>
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 16 }}>
                            <div style={{ fontWeight: 700, color: h.delta < 0 ? "#A32D2D" : "#3B6D11", fontSize: 13 }}>
                              {h.delta > 0 ? "+" : ""}{h.delta}j
                            </div>
                            <div style={{ fontSize: 10, color: "#bbb" }}>
                              {h.solde_apres != null ? `→ ${h.solde_apres}j` : ""} · {new Date(h.created_at).toLocaleDateString("fr-FR")}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Soldes;
